import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Car,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TagBadge } from '@/components/shared/TagBadge';
import { ContractorEditDialog, type ContractorEditForm } from './ContractorEditDialog';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { canSeeTurnover } from '@/lib/permissions';
import { useContractorsApi, useTagsApi, useCompaniesApi } from '@/services/api';
import type {
  ContractorListItem,
  ContractorDetail,
  ContractorRole,
  Tag as TagType,
  UpdateContractorDto,
  ContractorBankAccountData,
} from '@ip/shared-types';

/** Role badge: derived supplier/customer/both marker on a contractor row. */
/**
 * Turnover cells (decision 2026-09-10): `null` = the caller may not see the
 * money at all (no expense/sales module) → '—'; zero → '-'; otherwise PLN.
 * One rule for every amount and count column so the "no access" vs "nothing
 * yet" distinction can never drift between them.
 */
const moneyCell = (value: number | null) =>
  value === null ? '—' : value > 0 ? formatPLN(value) : '-';
const countCell = (value: number | null) => value ?? '—';

function RoleBadge({ role }: { role: ContractorRole }) {
  const styles: Record<ContractorRole, string> = {
    supplier: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    customer: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    both: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    none: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={`inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${styles[role]}`}>
      {t(`contractors.role.${role}`)}
    </span>
  );
}

interface ContractorListProps {
  companyNip: string;
  companyName: string;
  onCompanyNameLoad?: (name: string) => void;
}

function SortableHeader({
  label,
  column,
}: {
  label: string;
  column: Column<ContractorListItem, unknown>;
}) {
  const sorted = column.getIsSorted();
  return (
    <button
      type="button"
      className="hover:text-foreground -ml-1 flex items-center gap-1 rounded px-1 py-0.5"
      onClick={() => column.toggleSorting()}
    >
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="size-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="size-3.5" />
      ) : (
        <ArrowUpDown className="size-3.5 opacity-40" />
      )}
    </button>
  );
}

export function ContractorList({
  companyNip,
  companyName,
  onCompanyNameLoad,
}: ContractorListProps) {
  const [contractors, setContractors] = useState<ContractorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'supplier' | 'customer'>('all');
  const [tags, setTags] = useState<TagType[]>([]);
  const [expenseTags, setExpenseTags] = useState<TagType[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState<ContractorDetail | null>(null);
  const [saving, setSaving] = useState(false);

  // Turnover/counters are invoice data (decision 2026-09-10): without the
  // 'expense'/'sales' module the backend sends them as null, so the columns
  // are dropped entirely rather than rendered as a wall of dashes.
  const { permissions } = useAuth();
  const showTurnover = canSeeTurnover(permissions, companyNip);

  const { getContractors, getContractor, updateContractor, updateContractorTags } =
    useContractorsApi();
  const { getTags } = useTagsApi();
  const { getCompanyDetail } = useCompaniesApi();

  useEffect(() => {
    async function loadData() {
      if (!companyNip) return;

      setLoading(true);
      try {
        const [contractorsData, tagsData, expenseTagsData] = await Promise.all([
          getContractors(companyNip),
          getTags(companyNip, 'contractors'),
          getTags(companyNip, 'expense'),
        ]);
        setContractors(contractorsData);
        setTags(tagsData);
        setExpenseTags(expenseTagsData);

        if (!companyName && onCompanyNameLoad && companyNip !== 'other') {
          const companyDetail = await getCompanyDetail(companyNip);
          if (companyDetail.name) {
            onCompanyNameLoad(companyDetail.name);
          }
        }
      } catch (error) {
        console.error('Failed to load contractors:', error);
        toast.error(t('errors.loadingFailed'));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [companyNip, getContractors, getTags, getCompanyDetail, companyName, onCompanyNameLoad]);

  const filteredContractors = useMemo(() => {
    let result = contractors;
    // 'supplier'/'customer' include contractors that are BOTH — the filter
    // answers "who do we buy from / sell to", not "who is exclusively one".
    if (roleFilter !== 'all') {
      result = result.filter((c) => c.role === roleFilter || c.role === 'both');
    }
    if (!searchText) return result;
    const search = searchText.toLowerCase();
    return result.filter(
      (c) =>
        c.name?.toLowerCase().includes(search) ||
        c.nip?.toLowerCase().includes(search) ||
        c.erpId?.toLowerCase().includes(search) ||
        c.street?.toLowerCase().includes(search) ||
        c.city?.toLowerCase().includes(search),
    );
  }, [contractors, searchText, roleFilter]);

  const handleEditClick = useCallback(
    async (contractor: ContractorListItem) => {
      try {
        const detail = await getContractor(companyNip, contractor.id);
        setEditingContractor(detail);
        setEditDialogOpen(true);
      } catch (error) {
        console.error('Failed to load contractor detail:', error);
        toast.error(t('errors.loadingFailed'));
      }
    },
    [companyNip, getContractor],
  );

  const handleEditSave = useCallback(
    async (form: ContractorEditForm, accounts: ContractorBankAccountData[]) => {
      if (!editingContractor) return;

      try {
        setSaving(true);
        // Derive bankAccounts (string[]) from structured accounts for backwards compat
        const bankAccountStrings = accounts.map((a) => a.iban);
        const dto: UpdateContractorDto = {
          name: form.name || null,
          nip: form.nip || null,
          erpId: form.erpId || null,
          street: form.street || null,
          postalCode: form.postalCode || null,
          city: form.city || null,
          country: form.country || null,
          bankAccounts: bankAccountStrings,
          structuredBankAccounts: accounts.map(({ iban, swiftBic, currency, label }) => ({
            iban,
            swiftBic,
            currency,
            label,
          })),
          tags: form.tags || [],
          defaultInvoiceTagIds: form.defaultInvoiceTagIds || [],
        };
        await updateContractor(companyNip, editingContractor.id, dto);
        toast.success(t('common.save') + ' OK');
        setEditDialogOpen(false);

        const contractorsData = await getContractors(companyNip);
        setContractors(contractorsData);
      } catch (error) {
        console.error('Failed to update contractor:', error);
        toast.error(t('errors.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [editingContractor, companyNip, updateContractor, getContractors],
  );

  const handleRegistryRefreshed = useCallback(async () => {
    try {
      const contractorsData = await getContractors(companyNip);
      setContractors(contractorsData);
    } catch (error) {
      console.error('Failed to reload contractors after registry refresh:', error);
    }
  }, [companyNip, getContractors]);

  const handleTagsChange = useCallback(
    async (contractor: ContractorListItem, tagIds: string[]) => {
      try {
        await updateContractorTags(companyNip, contractor.id, tagIds);
        setContractors((prev) =>
          prev.map((c) => (c.id === contractor.id ? { ...c, tags: tagIds } : c)),
        );
        toast.success('Tags updated');
      } catch (error) {
        console.error('Failed to update tags:', error);
        toast.error(t('errors.saveFailed'));
      }
    },
    [companyNip, updateContractorTags],
  );

  const handleIsByCarChange = useCallback(
    async (contractor: ContractorListItem, checked: boolean) => {
      try {
        await updateContractor(companyNip, contractor.id, { isByCar: checked });
        setContractors((prev) =>
          prev.map((c) => (c.id === contractor.id ? { ...c, isByCar: checked } : c)),
        );
      } catch (error) {
        console.error('Failed to update isByCar:', error);
        toast.error(t('errors.saveFailed'));
      }
    },
    [companyNip, updateContractor],
  );

  const getTagById = useCallback((id: string) => tags.find((tag) => tag.id === id), [tags]);

  const columns = useMemo<ColumnDef<ContractorListItem>[]>(
    () => [
      {
        id: 'index',
        size: 50,
        header: () => t('contractors.columns.index'),
        cell: ({ row, table }) => {
          const { pageIndex, pageSize } = table.getState().pagination;
          const indexOnPage = table.getRowModel().rows.findIndex((r) => r.id === row.id);
          return (
            <span className="tabular-nums">{pageIndex * pageSize + indexOnPage + 1}</span>
          );
        },
      },
      {
        id: 'name',
        size: 200,
        header: () => t('contractors.columns.name'),
        cell: ({ row }) => (
          <Link
            to={`/${companyNip}/contractors/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary block max-w-56 truncate text-sm font-medium"
            title={row.original.name || '-'}
          >
            {row.original.name || '-'}
          </Link>
        ),
      },
      {
        id: 'nip',
        size: 120,
        header: () => t('contractors.columns.nip'),
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.nip || '-'}</span>
        ),
      },
      {
        id: 'role',
        size: 90,
        header: () => t('contractors.columns.role'),
        cell: ({ row }) => <RoleBadge role={row.original.role} />,
      },
      {
        id: 'address',
        size: 200,
        header: () => t('contractors.columns.address'),
        cell: ({ row }) => {
          const parts = [row.original.street, row.original.postalCode, row.original.city].filter(
            Boolean,
          );
          const address = parts.join(', ');
          return (
            <span className="block max-w-56 truncate" title={address || '-'}>
              {address || '-'}
            </span>
          );
        },
      },
      {
        id: 'bankAccounts',
        size: 180,
        header: () => t('contractors.columns.bankAccounts'),
        cell: ({ row }) => {
          const accounts = row.original.bankAccounts;
          if (!accounts || accounts.length === 0) return '-';
          return (
            <span
              className="block max-w-44 truncate font-mono text-xs"
              title={accounts.join('\n')}
            >
              {accounts[0]}
              {accounts.length > 1 && ` (+${accounts.length - 1})`}
            </span>
          );
        },
      },
      {
        id: 'tags',
        size: 200,
        header: () => t('contractors.columns.tags'),
        cell: ({ row }) => (
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex min-h-[22px] cursor-pointer flex-wrap items-center gap-1">
                {row.original.tags.map((id) => {
                  const tag = getTagById(id);
                  return tag ? <TagBadge key={id} name={tag.name} color={tag.color} /> : null;
                })}
                {row.original.tags.length === 0 && (
                  <span className="text-muted-foreground inline-flex items-center gap-0.5 rounded-md border border-dashed px-2 py-0.5 text-xs">
                    <Plus className="size-3" /> Add
                  </span>
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-56 space-y-2" align="start">
              {tags.map((tag) => (
                <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
                  <Checkbox
                    checked={row.original.tags.includes(tag.id)}
                    onCheckedChange={(checked) => {
                      const next =
                        checked === true
                          ? [...row.original.tags, tag.id]
                          : row.original.tags.filter((id) => id !== tag.id);
                      handleTagsChange(row.original, next);
                    }}
                  />
                  <TagBadge name={tag.name} color={tag.color} />
                </Label>
              ))}
              {tags.length === 0 && (
                <p className="text-muted-foreground text-sm">No tags available</p>
              )}
            </PopoverContent>
          </Popover>
        ),
      },
      // Turnover columns only for callers with invoice access — the fields
      // come back null otherwise (canSeeTurnover, decision 2026-09-10).
      ...(showTurnover
        ? ([
            {
              accessorKey: 'invoiceCount',
              id: 'invoiceCount',
              size: 80,
              header: ({ column }) => (
                <div className="flex justify-center">
                  <SortableHeader label={t('contractors.columns.invoiceCount')} column={column} />
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-center tabular-nums">{countCell(row.original.invoiceCount)}</div>
              ),
            },
            {
              accessorKey: 'yearlyTurnoverNet',
              id: 'yearlyTurnoverNet',
              size: 120,
              header: ({ column }) => (
                <div className="flex justify-end">
                  <SortableHeader label={t('contractors.columns.yearlyTurnoverNet')} column={column} />
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-right font-mono text-sm tabular-nums">
                  {moneyCell(row.original.yearlyTurnoverNet)}
                </div>
              ),
            },
            {
              accessorKey: 'monthlyTurnoverNet',
              id: 'monthlyTurnoverNet',
              size: 120,
              header: ({ column }) => (
                <div className="flex justify-end">
                  <SortableHeader label={t('contractors.columns.monthlyTurnoverNet')} column={column} />
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-right font-mono text-sm tabular-nums">
                  {moneyCell(row.original.monthlyTurnoverNet)}
                </div>
              ),
            },
            {
              accessorKey: 'salesInvoiceCount',
              id: 'salesInvoiceCount',
              size: 80,
              header: ({ column }) => (
                <div className="flex justify-center">
                  <SortableHeader label={t('contractors.columns.salesInvoiceCount')} column={column} />
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-center tabular-nums">{countCell(row.original.salesInvoiceCount)}</div>
              ),
            },
            {
              accessorKey: 'salesYearlyTurnoverNet',
              id: 'salesYearlyTurnoverNet',
              size: 120,
              header: ({ column }) => (
                <div className="flex justify-end">
                  <SortableHeader label={t('contractors.columns.salesYearlyTurnoverNet')} column={column} />
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-right font-mono text-sm tabular-nums">
                  {moneyCell(row.original.salesYearlyTurnoverNet)}
                </div>
              ),
            },
            {
              accessorKey: 'lastInvoiceDate',
              id: 'lastInvoiceDate',
              size: 110,
              sortingFn: (a, b) =>
                (a.original.lastInvoiceDate || '').localeCompare(b.original.lastInvoiceDate || ''),
              header: ({ column }) => (
                <SortableHeader label={t('contractors.columns.lastInvoiceDate')} column={column} />
              ),
              cell: ({ row }) => (
                <span className="tabular-nums">{row.original.lastInvoiceDate || '-'}</span>
              ),
            },
          ] as ColumnDef<ContractorListItem>[])
        : []),
      {
        id: 'isByCar',
        size: 60,
        header: () => (
          <div className="flex justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Car className="size-4" />
              </TooltipTrigger>
              <TooltipContent>{t('mileage.isByCar')}</TooltipContent>
            </Tooltip>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Switch
              checked={row.original.isByCar}
              onCheckedChange={(checked) => handleIsByCarChange(row.original, checked)}
            />
          </div>
        ),
      },
      {
        id: 'actions',
        size: 80,
        header: () => t('contractors.columns.actions'),
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => handleEditClick(row.original)}
              >
                <Pencil className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('contractors.actions.edit')}</TooltipContent>
          </Tooltip>
        ),
      },
    ],
    // Handlery w zależnościach — companyNip może się zmienić bez remountu
    // (przełączenie firmy w sidebarze) i stale closure zapisywałby w złej firmie.
    [
      tags,
      companyNip,
      showTurnover,
      getTagById,
      handleTagsChange,
      handleIsByCarChange,
      handleEditClick,
    ],
  );

  const table = useReactTable({
    data: filteredContractors,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: 20 } },
    // Parytet z poprzednim UI: mutacje wierszy (toggle tagu, isByCar, refetch po zapisie) tworzą nową
    // tablicę danych, a domyślny autoResetPageIndex cofałby wtedy tabelę na stronę 1.
    autoResetPageIndex: false,
  });

  // Reset strony tylko przy zmianie wyszukiwania — jedyny pożądany przypadek resetu.
  // Instancja `table` jest stabilna między renderami, więc efekt odpala się tylko na searchText.
  useEffect(() => {
    table.resetPageIndex();
  }, [searchText, roleFilter, table]);

  // Clamp: gdy dane się skurczą (filtr/refetch), nie zostawiaj użytkownika na pustej stronie.
  useEffect(() => {
    const { pageIndex: currentPage } = table.getState().pagination;
    const maxPage = Math.max(0, table.getPageCount() - 1);
    if (currentPage > maxPage) {
      table.setPageIndex(maxPage);
    }
  }, [filteredContractors.length, table]);

  const { pageIndex, pageSize } = table.getState().pagination;
  const total = filteredContractors.length;
  const totalPages = Math.max(1, table.getPageCount());
  const page = pageIndex + 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder={t('contractors.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9"
          />
        </div>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={roleFilter}
          onValueChange={(v) => v && setRoleFilter(v as 'all' | 'supplier' | 'customer')}
          aria-label={t('contractors.role.filterLabel')}
        >
          <ToggleGroupItem value="all" className="px-3">{t('contractors.role.all')}</ToggleGroupItem>
          <ToggleGroupItem value="supplier" className="px-3">{t('contractors.role.suppliers')}</ToggleGroupItem>
          <ToggleGroupItem value="customer" className="px-3">{t('contractors.role.customers')}</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.column.columnDef.size }}
                      className="text-muted-foreground whitespace-nowrap"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={columns.length}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-40 text-center">
                    <p className="font-medium">{t('contractors.noContractors')}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('contractors.noContractorsDescription')}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
          <p className="text-muted-foreground text-sm">{`${total} contractors`}</p>
          <div className="flex items-center gap-2">
            <Select value={String(pageSize)} onValueChange={(v) => table.setPageSize(Number(v))}>
              <SelectTrigger size="sm" className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!table.getCanPreviousPage()}
                onClick={() => table.setPageIndex(0)}
              >
                <ChevronsLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!table.getCanPreviousPage()}
                onClick={() => table.previousPage()}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="px-2 text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!table.getCanNextPage()}
                onClick={() => table.nextPage()}
              >
                <ChevronRight className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!table.getCanNextPage()}
                onClick={() => table.setPageIndex(totalPages - 1)}
              >
                <ChevronsRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ContractorEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contractor={editingContractor}
        companyNip={companyNip}
        tags={tags}
        expenseTags={expenseTags}
        saving={saving}
        onSave={handleEditSave}
        onRefreshed={handleRegistryRefreshed}
      />
    </div>
  );
}
